import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Home, Building2, Store, Info, Phone, Calendar, 
  Menu, X, LogIn, LayoutDashboard, Users, Settings, 
  Plus, Edit, Trash2, MapPin, Check, ArrowUpDown, 
  BedDouble, Bath, ArrowLeft, Send, Loader2, 
  MessageCircle, Facebook, Twitter, Instagram, Globe, UserCheck,
  Sparkles, Heart, GitCompare, Printer, Filter, PlayCircle, BarChart3, Inbox,
  Share2, UploadCloud, ChevronDown
} from 'lucide-react';

// --- API Configuration ---
const API_BASE_URL = 'https://rgyrealestate.com/api'; 

// --- Default Data ---
const initialSettings = {
  siteNameAr: 'بناة الحلم',
  siteNameEn: 'Dream Builders',
  aboutUs: 'شركة بناة الحلم هي خيارك الأول للبحث عن العقار المثالي.',
  aboutUsEn: 'Dream Builders is your first choice for finding the perfect property.',
  contactEmail: 'info@dreambuilders.com',
  contactPhone: '201000000000',
  appointmentPhone: '201111111111',
  address: 'دمياط، السنانية، جمهورية مصر العربية',
  addressEn: 'Damietta, Senaneya, Egypt',
  logoUrl: '',
  facebook: '',
  twitter: '',
  instagram: '',
  geminiApiKey: '', 
  menuItems: [
    { id: 1, labelAr: 'الرئيسية', labelEn: 'Home', link: 'home', type: 'internal' },
    { id: 2, labelAr: 'وحدات سكنية', labelEn: 'Residential', link: 'properties', type: 'internal' },
    { id: 3, labelAr: 'تجاري وإداري', labelEn: 'Commercial', link: 'shops', type: 'internal' },
    { id: 4, labelAr: 'من نحن', labelEn: 'About Us', link: 'about', type: 'internal' },
    { id: 5, labelAr: 'اتصل بنا', labelEn: 'Contact Us', link: 'contact', type: 'internal' }
  ],
  propertyCategories: [
    { 
      id: 'residential', nameAr: 'وحدات سكنية', nameEn: 'Residential', 
      subCategoriesAr: ['شقق', 'بيوت', 'فيلات', 'كومبوندات'],
      subCategoriesEn: ['Apartments', 'Houses', 'Villas', 'Compounds']
    },
    { 
      id: 'commercial', nameAr: 'تجاري وإداري', nameEn: 'Commercial', 
      subCategoriesAr: ['محلات', 'مولات تجارية', 'مكاتب إدارية', 'عيادات'],
      subCategoriesEn: ['Shops', 'Malls', 'Offices', 'Clinics']
    }
  ]
};

const initialUsers = [
  { id: 1, name: 'السيد شلبي', username: 'admin', password: '123', role: 'مدير', phone: '201012345678' }
];

// --- Translations Dictionary ---
const tr = {
  ar: {
    home: 'الرئيسية', residential: 'وحدات سكنية', commercial: 'تجاري وإداري', about: 'من نحن', contact: 'اتصل بنا',
    book_appointment: 'حجز موعد', admin_login: 'دخول الإدارة', search_dream: 'ابحث عن عقارك المثالي مع',
    best_offers: 'أفضل العروض للشقق والفيلات والمحلات التجارية بيع أو إيجار بتشطيبات تناسب جميع الأذواق.',
    browse_props: 'تصفح العقارات', browse_shops: 'تصفح المحلات', latest_added: 'أحدث الوحدات',
    discover_latest: 'اكتشف أحدث العروض الحصرية لدينا', all_props: 'جميع الوحدات السكنية', all_shops: 'الوحدات التجارية والإدارية',
    contact_seller: 'تواصل مع البائع', no_units: 'لا توجد وحدات مطابقة للبحث.', currency: 'ج.م', month: '/ شهرياً', 
    rooms: 'غرف', baths: 'حمام', elevator: 'يوجد مصعد', no_elevator: 'بدون مصعد', city_filter: 'تصفية بالمدينة', 
    all_cities: 'جميع المدن', quick_links: 'روابط سريعة', connect_us: 'تواصل معنا', rights: 'جميع الحقوق محفوظة', 
    floor: 'الدور', finish_type: 'نوع التشطيب', master_bath: 'حمام ماستر', yes: 'نعم', no: 'لا', ground: 'أرضي', 
    back: 'العودة للقائمة', req_price: 'السعر المطلوب', print: 'طباعة', wishlist: 'المفضلة', 
    compare: 'مقارنة', addToCompare: 'أضف للمقارنة', view_tour: 'جولة بالفيديو', inquire: 'طلب استفسار',
    name: 'الاسم', phone: 'رقم الهاتف', msg: 'رسالتك', send_inquiry: 'إرسال الطلب', filters: 'فلاتر متقدمة',
    priceMin: 'السعر من', priceMax: 'السعر إلى', minRooms: 'عدد الغرف +', compare_props: 'مقارنة العقارات',
    clear: 'مسح', close: 'إغلاق', saved_to_wishlist: 'تم الحفظ في المفضلة', inquiry_sent: 'تم إرسال طلبك بنجاح!',
    overview: 'نظرة عامة', leads: 'الطلبات', share: 'مشاركة', share_success: 'تم نسخ رابط العقار بنجاح!'
  },
  en: {
    home: 'Home', residential: 'Residential', commercial: 'Commercial', about: 'About Us', contact: 'Contact Us',
    book_appointment: 'Book Appt', admin_login: 'Admin Login', search_dream: 'Find your perfect property with',
    best_offers: 'Best offers for apartments, villas, and shops for sale or rent.',
    browse_props: 'Browse Properties', browse_shops: 'Browse Shops', latest_added: 'Latest Units',
    discover_latest: 'Discover our latest exclusive offers', all_props: 'All Residential Units', all_shops: 'Commercial Units',
    contact_seller: 'Contact Seller', no_units: 'No units match your search.', currency: 'EGP', month: '/ month', 
    rooms: 'Rooms', baths: 'Baths', elevator: 'Elevator', no_elevator: 'No Elevator', city_filter: 'Filter by City', 
    all_cities: 'All Cities', quick_links: 'Quick Links', connect_us: 'Connect With Us', rights: 'All rights reserved', 
    floor: 'Floor', finish_type: 'Finishing Type', master_bath: 'Master Bath', yes: 'Yes', no: 'No', ground: 'Ground', 
    back: 'Back to list', req_price: 'Asking Price', print: 'Print', wishlist: 'Wishlist', 
    compare: 'Compare', addToCompare: 'Add to Compare', view_tour: 'Video Tour', inquire: 'Inquire Now',
    name: 'Name', phone: 'Phone', msg: 'Message', send_inquiry: 'Send Inquiry', filters: 'Advanced Filters',
    priceMin: 'Min Price', priceMax: 'Max Price', minRooms: 'Min Rooms', compare_props: 'Compare Properties',
    clear: 'Clear', close: 'Close', saved_to_wishlist: 'Saved to Wishlist', inquiry_sent: 'Inquiry sent successfully!',
    overview: 'Overview', leads: 'Leads', share: 'Share', share_success: 'Property link copied successfully!'
  }
};

export default function DreamBuildersApp() {
  const [isDataLoading, setIsDataLoading] = useState(true);
  
  const [lang, setLang] = useState('ar'); 
  const t = (key) => tr[lang][key] || key;
  const isRTL = lang === 'ar';

  const [currentView, setCurrentView] = useState('home');
  const [currentUser, setCurrentUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem('dreambuilders_wishlist') || '[]'));
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ city: '', minPrice: '', maxPrice: '', minRooms: '' });

  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState(initialUsers);
  const [settings, setSettings] = useState(initialSettings);
  const [clients, setClients] = useState([]); 
  const [leads, setLeads] = useState([]); 

  const [adminTab, setAdminTab] = useState('overview'); 
  const [isEditingData, setIsEditingData] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  
  // State for tracking sub-category filter from Dropdown Menu
  const [selectedSubCategory, setSelectedSubCategory] = useState('');

  const safeMenuItems = settings.menuItems?.length ? settings.menuItems : initialSettings.menuItems;
  const siteBrand = isRTL ? (settings.siteNameAr || initialSettings.siteNameAr) : (settings.siteNameEn || initialSettings.siteNameEn);
  const propertyCategories = settings.propertyCategories || initialSettings.propertyCategories;

  // --- Admin Route Handler ---
  useEffect(() => {
    if (window.location.hash === '#admin') {
      setCurrentView('admin_login');
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_all.php`);
        if (response.ok) {
          const data = await response.json();
          if (data.properties) setProperties(data.properties);
          if (data.users && data.users.length > 0) setUsers(data.users); 
          if (data.settings) setSettings({...initialSettings, ...data.settings});
          if (data.clients) setClients(data.clients);
          if (data.leads) setLeads(data.leads);
        }
      } catch (error) {
        console.log("استخدام البيانات الافتراضية للخطأ في جلب البيانات");
      } finally {
        setIsDataLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!isDataLoading && properties.length > 0 && !selectedProperty) {
      const params = new URLSearchParams(window.location.search);
      const propId = params.get('property_id');
      if (propId) {
        const prop = properties.find(p => p.id.toString() === propId);
        if (prop) setSelectedProperty(prop);
      }
    }
  }, [isDataLoading, properties]);

  useEffect(() => {
    if (selectedProperty) {
      const propTitle = isRTL ? selectedProperty.title : (selectedProperty.titleEn || selectedProperty.title);
      document.title = `${propTitle} - ${siteBrand}`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = isRTL ? selectedProperty.description : (selectedProperty.descriptionEn || selectedProperty.description);
    } else {
      document.title = siteBrand;
    }
  }, [selectedProperty, isRTL, siteBrand]);

  useEffect(() => {
    localStorage.setItem('dreambuilders_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // Navigate function updated to support Sub-Category filtering
  const navigate = (view, subCat = '') => {
    setCurrentView(view);
    setSelectedSubCategory(subCat);
    setIsMobileMenuOpen(false);
    setSelectedProperty(null);
    setSelectedFilterCity('');
    setLoginError('');
    setSuccessMsg('');
    window.history.pushState({}, '', window.location.pathname);
    window.scrollTo(0, 0);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const username = e.target.username.value;
    const password = e.target.password.value;
    const user = (users || []).find(u => u.username === username && u.password === password);
    if (user) {
      setCurrentUser(user);
      navigate('admin_dashboard');
      setAdminTab('overview');
    } else {
      setLoginError(isRTL ? 'بيانات الدخول غير صحيحة.' : 'Invalid credentials.');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    navigate('home');
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const toggleWishlist = (e, propId) => {
    e.stopPropagation();
    if (wishlist.includes(propId)) setWishlist(wishlist.filter(id => id !== propId));
    else { setWishlist([...wishlist, propId]); showSuccess(t('saved_to_wishlist')); }
  };

  const toggleCompare = (e, prop) => {
    e.stopPropagation();
    if (compareList.find(p => p.id === prop.id)) setCompareList(compareList.filter(p => p.id !== prop.id));
    else {
      if (compareList.length >= 3) { alert(isRTL ? 'يمكنك مقارنة 3 عقارات كحد أقصى' : 'Max 3 properties for comparison'); return; }
      setCompareList([...compareList, prop]);
    }
  };

  const handleLeadSubmit = async (e, propId, agentId) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newLead = {
      id: Date.now(), name: formData.get('name'), phone: formData.get('phone'), message: formData.get('message'),
      propertyId: propId, agentId: agentId, status: 'جديد', date: new Date().toISOString()
    };
    try {
      await fetch(`${API_BASE_URL}/leads.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newLead) });
      setLeads([newLead, ...(leads || [])]); showSuccess(t('inquiry_sent')); e.target.reset();
    } catch (err) { setLeads([newLead, ...(leads || [])]); showSuccess(t('inquiry_sent')); e.target.reset(); }
  };

  const availableCities = useMemo(() => {
    const list = (properties || []).map(p => isRTL ? p.city : (p.cityEn || p.city)).filter(Boolean);
    return [...new Set(list)];
  }, [properties, isRTL]);

  const FloatingWhatsApp = () => (
    <a href={`https://wa.me/${settings.appointmentPhone}`} target="_blank" rel="noreferrer" className={`fixed bottom-6 print:hidden ${isRTL ? 'left-6' : 'right-6'} bg-green-500 text-white p-4 rounded-full shadow-2xl z-40 hover:bg-green-600 transition-transform hover:scale-110 flex items-center justify-center`}>
      <MessageCircle size={32} />
    </a>
  );

  const CompareFloatingBar = () => {
    if (compareList.length === 0) return null;
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white p-4 z-50 flex items-center justify-between print:hidden shadow-2xl animate-in slide-in-from-bottom-full">
        <div className="flex items-center gap-4">
          <span className="font-bold">{compareList.length} / 3 {isRTL ? 'محددة للمقارنة' : 'Selected for Compare'}</span>
          <div className="flex gap-2">
            {compareList.map(p => (<span key={p.id} className="text-xs bg-slate-800 px-2 py-1 rounded truncate max-w-[100px] hidden md:block">{isRTL ? p.title : (p.titleEn || p.title)}</span>))}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setCompareList([])} className="text-slate-400 hover:text-white px-3">{t('clear')}</button>
          <button onClick={() => setShowCompareModal(true)} className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-bold">{t('compare')}</button>
        </div>
      </div>
    );
  };

  const CompareModal = () => {
    if (!showCompareModal) return null;
    return (
      <div className="fixed inset-0 bg-slate-900/80 z-50 flex items-center justify-center p-4 print:hidden">
        <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-6 relative">
          <button onClick={() => setShowCompareModal(false)} className="absolute top-6 right-6 text-slate-500 hover:text-red-500"><X size={28}/></button>
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-2"><GitCompare /> {t('compare_props')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {compareList.map(p => (
              <div key={p.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                <img src={p.image} alt="" className="h-48 w-full object-cover bg-slate-100" />
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                  <h3 className="font-bold text-lg h-14 overflow-hidden">{isRTL ? p.title : (p.titleEn || p.title)}</h3>
                  <p className="text-blue-600 font-bold text-xl">{p.price.toLocaleString()} {t('currency')}</p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('city_filter')}</span><span className="font-medium">{isRTL ? p.city : (p.cityEn || p.city)}</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('rooms')}</span><span className="font-medium">{p.specs?.rooms}</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('baths')}</span><span className="font-medium">{p.specs?.bathrooms}</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('floor')}</span><span className="font-medium">{p.specs?.floor === 0 ? t('ground') : p.specs?.floor}</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('finish_type')}</span><span className="font-medium">{p.specs?.finishing}</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-500">{t('elevator')}</span><span>{p.specs?.hasElevator ? <Check className="text-green-500"/> : <X className="text-red-500"/>}</span></div>
                </div>
                <div className="p-4">
                  <button onClick={() => { setShowCompareModal(false); setSelectedProperty(p); window.history.pushState({}, '', `?property_id=${p.id}`); }} className="w-full bg-blue-50 text-blue-600 py-2 rounded-xl font-bold hover:bg-blue-100">عرض التفاصيل</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const PublicNavbar = () => (
    <nav className="bg-white shadow-sm sticky top-0 z-40 print:hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('home')}>
            {settings.logoUrl ? <img src={settings.logoUrl} alt="Logo" className="h-10 object-contain" /> : <Building2 className="h-8 w-8 text-blue-600" />}
            <span className="font-bold text-2xl text-slate-800 hidden sm:block">{siteBrand}</span>
          </div>
          
          <div className="hidden md:flex items-center gap-6 h-full">
            {/* Dynamic Menu */}
            {safeMenuItems.map(item => {
              if (item.type === 'internal') {
                const isProps = item.link === 'properties';
                const isShops = item.link === 'shops';
                const hasDropdown = isProps || isShops;
                const catId = isProps ? 'residential' : 'commercial';
                const activeCat = propertyCategories.find(c => c.id === catId);
                const subCatsAr = activeCat?.subCategoriesAr || activeCat?.subCategories || [];
                const subCatsEn = activeCat?.subCategoriesEn || [];

                return (
                  <div key={item.id} className="relative group h-full flex items-center">
                    <button onClick={() => navigate(item.link)} className={`font-medium transition-colors flex items-center gap-1 ${currentView === item.link && !selectedSubCategory ? 'text-blue-600' : 'text-slate-600 hover:text-blue-600'}`}>
                      {isRTL ? item.labelAr : item.labelEn}
                      {hasDropdown && subCatsAr.length > 0 && <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />}
                    </button>
                    {hasDropdown && subCatsAr.length > 0 && (
                      <>
                        {/* Bridge to prevent losing hover when moving mouse down */}
                        <div className="absolute top-[calc(50%+12px)] left-0 right-0 h-8 bg-transparent"></div>
                        <div className={`absolute top-[calc(100%-10px)] ${isRTL ? 'right-0' : 'left-0'} w-48 bg-white border border-slate-100 shadow-xl rounded-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform origin-top translate-y-2 group-hover:translate-y-0`}>
                          {subCatsAr.map((subAr, idx) => {
                            const subEn = subCatsEn[idx] || subAr;
                            const displayName = isRTL ? subAr : subEn;
                            return (
                              <button key={subAr} onClick={(e) => { e.stopPropagation(); navigate(item.link, subAr); }} className={`block w-full ${isRTL?'text-right':'text-left'} px-4 py-2 hover:bg-slate-50 text-sm transition-colors ${selectedSubCategory === subAr ? 'text-blue-600 bg-blue-50/50 font-bold' : 'text-slate-700 hover:text-blue-600'}`}>
                                {displayName}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              }
              return (<a key={item.id} href={item.link} target="_blank" rel="noreferrer" className="font-medium text-slate-600 hover:text-blue-600 transition-colors h-full flex items-center">{isRTL ? item.labelAr : item.labelEn}</a>);
            })}
            
            <button onClick={() => navigate('wishlist')} className={`font-medium flex items-center gap-1 h-full ${currentView === 'wishlist' ? 'text-red-500' : 'text-slate-600 hover:text-red-500'}`}>
              <Heart size={18} className={wishlist.length > 0 ? 'fill-red-500 text-red-500' : ''} /> {t('wishlist')}
            </button>
            
            <div className="h-6 w-px bg-slate-200"></div>
            <button onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="flex items-center gap-1 text-slate-600 hover:text-blue-600 font-medium"><Globe size={18} /> {lang === 'ar' ? 'EN' : 'عربي'}</button>
            <a href={`https://wa.me/${settings.appointmentPhone}`} target="_blank" rel="noreferrer" className="bg-blue-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"><Calendar size={18} /> {t('book_appointment')}</a>
          </div>
          
          <div className="md:hidden flex items-center gap-4">
            <button onClick={() => navigate('wishlist')} className="text-slate-600"><Heart size={24} className={wishlist.length > 0 ? 'fill-red-500 text-red-500' : ''} /></button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-slate-600 p-2">{isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}</button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 pt-2 pb-6 space-y-2 shadow-lg absolute w-full z-50">
          {safeMenuItems.map(item => {
              if (item.type === 'internal') { 
                const isProps = item.link === 'properties';
                const isShops = item.link === 'shops';
                const hasDropdown = isProps || isShops;
                const catId = isProps ? 'residential' : 'commercial';
                const activeCat = propertyCategories.find(c => c.id === catId);
                const subCatsAr = activeCat?.subCategoriesAr || activeCat?.subCategories || [];
                const subCatsEn = activeCat?.subCategoriesEn || [];

                return (
                  <div key={item.id} className="border-b border-slate-50 last:border-0 pb-1">
                    <button onClick={() => navigate(item.link)} className={`block w-full ${isRTL?'text-right':'text-left'} py-2 font-medium ${currentView === item.link && !selectedSubCategory ? 'text-blue-600' : 'text-slate-700'}`}>
                      {isRTL ? item.labelAr : item.labelEn}
                    </button>
                    {hasDropdown && subCatsAr.length > 0 && (
                      <div className="flex flex-col gap-1 bg-slate-50 rounded-lg p-2 mt-1">
                        {subCatsAr.map((subAr, idx) => {
                          const subEn = subCatsEn[idx] || subAr;
                          const displayName = isRTL ? subAr : subEn;
                          return (
                            <button key={subAr} onClick={() => navigate(item.link, subAr)} className={`block w-full ${isRTL?'text-right':'text-left'} px-4 py-1.5 text-sm transition-colors ${selectedSubCategory === subAr ? 'text-blue-600 font-bold' : 'text-slate-600 hover:text-blue-600'}`}>
                              - {displayName}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ); 
              }
              return (<a key={item.id} href={item.link} target="_blank" rel="noreferrer" className={`block w-full ${isRTL?'text-right':'text-left'} py-2 font-medium text-slate-700`}>{isRTL ? item.labelAr : item.labelEn}</a>);
            })}
          <button onClick={() => navigate('wishlist')} className={`block w-full ${isRTL?'text-right':'text-left'} py-2 font-medium text-slate-700`}>{t('wishlist')}</button>
          <div className="flex justify-between items-center py-2 border-t mt-2">
            <button onClick={() => {setLang(lang === 'ar' ? 'en' : 'ar'); setIsMobileMenuOpen(false);}} className="flex items-center gap-2 text-slate-600 font-bold"><Globe size={18} /> {lang === 'ar' ? 'English' : 'عربي'}</button>
          </div>
          <a href={`https://wa.me/${settings.appointmentPhone}`} target="_blank" rel="noreferrer" className="block w-full text-center bg-blue-600 text-white py-3 rounded-xl font-medium mt-4 flex items-center justify-center gap-2">{t('book_appointment')} <Calendar size={18} /></a>
        </div>
      )}
    </nav>
  );

  const PropertyCard = ({ property }) => {
    const title = isRTL ? property.title : (property.titleEn || property.title);
    const city = isRTL ? property.city : (property.cityEn || property.city);
    const isWishlisted = wishlist.includes(property.id);
    const isCompared = compareList.find(p => p.id === property.id);

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer relative" onClick={() => {
        setSelectedProperty(property); window.history.pushState({}, '', `?property_id=${property.id}`);
      }}>
        <button onClick={(e) => toggleWishlist(e, property.id)} className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} z-10 p-2 rounded-full backdrop-blur shadow-sm transition-all ${isWishlisted ? 'bg-red-50 text-red-500' : 'bg-white/70 text-slate-500 hover:bg-white hover:text-red-500'}`}>
          <Heart size={20} className={isWishlisted ? 'fill-red-500' : ''} />
        </button>
        <div className="relative h-56 overflow-hidden bg-slate-100 flex items-center justify-center">
          {property.image ? <img src={property.image} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /> : <Building2 size={48} className="text-slate-300" />}
          <div className={`absolute top-4 ${isRTL ? 'right-4' : 'left-4'} flex flex-col gap-2`}>
            <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full shadow-md">{isRTL ? property.transaction : (property.transaction === 'بيع' ? 'Sale' : 'Rent')}</span>
            <span className="px-3 py-1 bg-white text-slate-800 text-xs font-bold rounded-full shadow-md">{city}</span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-xl font-bold text-slate-900 mb-2 truncate">{title}</h3>
          <p className="text-blue-600 font-bold text-xl mb-4">{property.price.toLocaleString()} {t('currency')}</p>
          <div className="flex items-center justify-between text-slate-500 text-sm border-t border-slate-100 pt-4">
            <span className="flex items-center gap-1"><BedDouble size={16} /> {property.specs?.rooms} {t('rooms')}</span>
            <span className="flex items-center gap-1"><Bath size={16} /> {property.specs?.bathrooms} {t('baths')}</span>
            <button onClick={(e) => toggleCompare(e, property)} className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${isCompared ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-100'}`} title={t('addToCompare')}>
              <GitCompare size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PropertyDetails = ({ property, onBack, showSuccess }) => {
    const title = isRTL ? property.title : (property.titleEn || property.title);
    const desc = isRTL ? property.description : (property.descriptionEn || property.description);
    const city = isRTL ? property.city : (property.cityEn || property.city);
    const isWishlisted = wishlist.includes(property.id);
    
    const handleShare = () => {
      const shareUrl = `${window.location.origin}${window.location.pathname}?property_id=${property.id}`;
      try {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
        showSuccess(t('share_success'));
      } catch (err) { console.error('Failed to copy', err); }
    };

    const getEmbedUrl = (url) => {
      if (!url) return '';
      try {
        let videoId = '';
        if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1]?.split('?')[0];
        else if (url.includes('watch?v=')) videoId = url.split('watch?v=')[1]?.split('&')[0];
        else if (url.includes('embed/')) return url;
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
        return url;
      } catch(e) { return url; }
    };

    const propAgent = (users || []).find(u => Number(u.id) === Number(property.agentId));
    const agentPhoneStr = propAgent?.phone || property.agentPhone || settings.appointmentPhone || '';
    const agentWhatsApp = agentPhoneStr.replace(/[^a-zA-Z0-9]/g, '');

    // تحديد اسم القسم الفرعي بناءً على اللغة (fallback للعربي إذا لم يوجد إنجليزي)
    let displaySubCategory = property.subCategoryEn || property.subCategory || '';
    if (isRTL) {
      displaySubCategory = property.subCategory || '';
    }

    return (
      <div className="max-w-5xl mx-auto px-4 py-8 animate-in fade-in zoom-in-95 duration-300">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 print:hidden">
          <button onClick={() => { onBack(); window.history.pushState({}, '', window.location.pathname); }} className={`flex items-center gap-2 text-slate-500 hover:text-blue-600 font-medium transition-colors ${isRTL ? '' : 'flex-row-reverse'}`}>
            <ArrowLeft size={20} className={isRTL ? '' : 'rotate-180'} /> {t('back')}
          </button>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <button onClick={handleShare} className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"><Share2 size={18} className="text-blue-600" /> <span className="hidden sm:inline">{t('share')}</span></button>
            <button onClick={(e) => toggleWishlist(e, property.id)} className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl font-medium transition-colors ${isWishlisted ? 'bg-red-50 text-red-500' : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-700'}`}><Heart size={18} className={isWishlisted ? 'fill-red-500' : ''} /> <span className="hidden sm:inline">{t('wishlist')}</span></button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"><Printer size={18} /> <span className="hidden sm:inline">{t('print')}</span></button>
          </div>
        </div>
        
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="h-64 md:h-[500px] relative print:h-64 bg-slate-100 flex justify-center items-center">
            {property.image ? <img src={property.image} alt={title} className="w-full h-full object-cover" /> : <Building2 size={64} className="text-slate-300" />}
          </div>
          
          <div className="p-6 md:p-10">
            <div className="flex flex-col md:flex-row justify-between gap-8 mb-8 border-b border-slate-100 pb-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4 text-slate-500 font-medium">
                  <MapPin size={18} /> {city} - {displaySubCategory || (isRTL ? property.category : 'Property')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{title}</h1>
                <p className="text-slate-600 leading-relaxed text-lg whitespace-pre-line">{desc}</p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-2xl md:min-w-[320px] h-fit border border-blue-100 print:hidden">
                <p className="text-sm text-blue-600 font-medium mb-1">{t('req_price')}</p>
                <p className="text-3xl font-bold text-slate-900 mb-6">{property.price.toLocaleString()} {t('currency')}</p>
                <form onSubmit={(e) => handleLeadSubmit(e, property.id, property.agentId)} className="space-y-3 border-t border-blue-200 pt-4">
                  <h4 className="font-bold text-slate-800 mb-2">{t('inquire')}</h4>
                  <input required name="name" placeholder={t('name')} className="w-full px-3 py-2 rounded-lg border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  <input required name="phone" placeholder={t('phone')} className="w-full px-3 py-2 rounded-lg border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 text-sm dir-ltr text-right" />
                  <textarea name="message" placeholder={t('msg')} rows="2" className="w-full px-3 py-2 rounded-lg border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 text-sm"></textarea>
                  <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-bold transition-all shadow-md">{t('send_inquiry')}</button>
                </form>
                <a href={`https://wa.me/${agentWhatsApp}?text=${encodeURIComponent(`${t('search_dream')} ${title} - ${property.price.toLocaleString()} ${t('currency')}\nرابط العقار: ${window.location.origin}${window.location.pathname}?property_id=${property.id}`)}`} target="_blank" rel="noreferrer" className="mt-3 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-xl font-bold transition-all shadow-md">
                  <Send size={18} className={isRTL ? 'rotate-180' : ''} /> {t('contact_seller')} (WhatsApp)
                </a>
              </div>
              <div className="hidden print:block text-2xl font-bold text-slate-900">{t('req_price')}: {property.price.toLocaleString()} {t('currency')}</div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><span className="text-slate-500 text-sm block mb-1">{t('floor')}</span><span className="font-bold text-slate-800 text-lg">{Number(property.specs?.floor) === 0 ? t('ground') : property.specs?.floor}</span></div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><span className="text-slate-500 text-sm block mb-1">{t('rooms')}</span><span className="font-bold text-slate-800 text-lg">{property.specs?.rooms}</span></div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><span className="text-slate-500 text-sm block mb-1">{t('baths')}</span><span className="font-bold text-slate-800 text-lg">{property.specs?.bathrooms}</span></div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><span className="text-slate-500 text-sm block mb-1">{t('finish_type')}</span><span className="font-bold text-slate-800 text-lg">{property.specs?.finishing}</span></div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center justify-between"><span className="text-slate-700 font-medium">{t('elevator')}</span>{property.specs?.hasElevator ? <Check className="text-green-500" /> : <X className="text-red-500" />}</div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center justify-between"><span className="text-slate-700 font-medium">{t('master_bath')}</span>{property.specs?.hasMasterBath ? <Check className="text-green-500" /> : <X className="text-red-500" />}</div>
            </div>

            {property.videoUrl && (
              <div className="mt-10 border-t border-slate-100 pt-8 print:hidden">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2"><PlayCircle className="text-red-500"/> {t('view_tour')}</h3>
                <div className="aspect-video w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-slate-900">
                  <iframe src={getEmbedUrl(property.videoUrl)} title="Virtual Tour" className="w-full h-full" allowFullScreen></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const PublicLayout = ({ children }) => (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col relative print:bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <PublicNavbar />
      <FloatingWhatsApp />
      <CompareFloatingBar />
      <CompareModal />
      {successMsg && <div className="fixed top-24 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-6 py-3 rounded-full shadow-lg font-bold flex items-center gap-2 z-50 animate-in slide-in-from-top-4 print:hidden"><Check size={20} /> {successMsg}</div>}
      <main className="flex-1">{children}</main>
      <footer className="bg-slate-900 text-white py-12 print:hidden">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-start">
          <div>
            <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
              {settings.logoUrl ? <img src={settings.logoUrl} alt="Logo" className="h-10 object-contain bg-white/10 p-1 rounded" /> : <Building2 className="h-8 w-8 text-blue-500" />}
              <span className="font-bold text-2xl">{siteBrand}</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">{(isRTL ? settings.aboutUs : (settings.aboutUsEn || settings.aboutUs)).substring(0, 150)}...</p>
          </div>
          <div>
            <h4 className="font-bold text-lg mb-4">{t('quick_links')}</h4>
            <ul className="space-y-2 text-slate-400">
              {safeMenuItems.filter(i=>i.type==='internal').map(item => (
                <li key={item.id}><button onClick={() => navigate(item.link)} className="hover:text-white transition-colors">{isRTL ? item.labelAr : item.labelEn}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-lg mb-4">{t('connect_us')}</h4>
            <ul className="space-y-2 text-slate-400">
              <li className="flex items-center justify-center md:justify-start gap-2 dir-ltr"><Phone size={16}/> {settings.contactPhone}</li>
              <li className="flex items-center justify-center md:justify-start gap-2"><MapPin size={16}/> {isRTL ? settings.address : (settings.addressEn || settings.address)}</li>
            </ul>
            <div className="flex items-center justify-center md:justify-start gap-4 mt-4">
              {settings.facebook && <a href={settings.facebook} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-500"><Facebook size={20}/></a>}
              {settings.twitter && <a href={settings.twitter} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-400"><Twitter size={20}/></a>}
              {settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-pink-500"><Instagram size={20}/></a>}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm flex flex-col md:flex-row justify-center items-center gap-1">
          <span>{t('rights')}</span>
          <a href="https://wpressly.com" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-bold">WPressly LTD</a>
        </div>
      </footer>
    </div>
  );

  // --- Admin Components ---
  const AdminSidebar = () => (
    <aside className="w-64 bg-slate-900 text-white h-screen fixed hidden md:flex flex-col z-20" dir="rtl">
      <div className="p-6 border-b border-slate-800 flex items-center gap-3">
        <Building2 className="text-blue-500" size={28} />
        <div>
          <h1 className="font-bold text-xl">إدارة النظام</h1>
          <p className="text-xs text-slate-400">{currentUser?.name} ({currentUser?.role})</p>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        <button onClick={() => {setAdminTab('overview'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'overview' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
          <BarChart3 size={20} /> {t('overview')}
        </button>
        <button onClick={() => {setAdminTab('properties'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'properties' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
          <Home size={20} /> إدارة الوحدات
        </button>
        <button onClick={() => {setAdminTab('leads'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'leads' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
          <Inbox size={20} /> الطلبات (Leads)
          {(leads || []).filter(l => l.status === 'جديد').length > 0 && <span className="mr-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{(leads || []).filter(l => l.status === 'جديد').length}</span>}
        </button>
        {currentUser?.role === 'مدير' && (
          <>
            <button onClick={() => {setAdminTab('clients'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'clients' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <UserCheck size={20} /> المبيعات والعملاء
            </button>
            <button onClick={() => {setAdminTab('users'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'users' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <Users size={20} /> فريق العمل
            </button>
            <button onClick={() => {setAdminTab('settings'); setIsEditingData(null);}} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${adminTab === 'settings' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <Settings size={20} /> الإعدادات
            </button>
          </>
        )}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white px-4 py-3 rounded-xl transition-colors font-medium">
          <LogIn size={18} className="rotate-180" /> تسجيل الخروج
        </button>
      </div>
    </aside>
  );

  const AdminOverview = () => {
    const totalProps = (properties || []).length;
    const totalLeads = (leads || []).length;
    const totalSales = (clients || []).reduce((acc, c) => acc + ((properties || []).find(p => p.id === c.propertyId)?.price || 0), 0);
    const totalCommissions = (clients || []).reduce((acc, c) => acc + (c.commission || 0), 0);

    return (
      <div className="space-y-6 animate-in fade-in">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="bg-blue-50 p-4 rounded-xl text-blue-600"><Building2 size={24} /></div>
            <div><p className="text-sm text-slate-500">إجمالي الوحدات</p><h3 className="text-2xl font-bold">{totalProps}</h3></div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="bg-orange-50 p-4 rounded-xl text-orange-600"><Inbox size={24} /></div>
            <div><p className="text-sm text-slate-500">طلبات الاستفسار</p><h3 className="text-2xl font-bold">{totalLeads}</h3></div>
          </div>
          {currentUser.role === 'مدير' && (
            <>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
                <div className="bg-green-50 p-4 rounded-xl text-green-600"><BarChart3 size={24} /></div>
                <div><p className="text-sm text-slate-500">حجم المبيعات التقديري</p><h3 className="text-xl font-bold">{totalSales.toLocaleString()} ج</h3></div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
                <div className="bg-purple-50 p-4 rounded-xl text-purple-600"><UserCheck size={24} /></div>
                <div><p className="text-sm text-slate-500">إجمالي العمولات</p><h3 className="text-xl font-bold">{totalCommissions.toLocaleString()} ج</h3></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const AdminLeads = () => {
    const handleStatusChange = async (leadId, newStatus) => {
      try {
        await fetch(`${API_BASE_URL}/leads.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'update_status', id: leadId, status: newStatus })
        });
        setLeads((leads || []).map(l => l.id === leadId ? {...l, status: newStatus} : l));
        showSuccess('تم تحديث حالة الطلب');
      } catch (err) { console.error(err); }
    };

    const handleDeleteLead = async (id) => {
      if(window.confirm('مسح الطلب؟')) {
        try {
          await fetch(`${API_BASE_URL}/leads.php?id=${id}`, { method: 'DELETE' });
          setLeads((leads || []).filter(l => l.id !== id));
          showSuccess('تم مسح الطلب');
        } catch (err) { console.error(err); }
      }
    };

    const displayLeads = currentUser.role === 'مدير' ? (leads || []) : (leads || []).filter(l => Number(l.agentId) === Number(currentUser.id));

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <h3 className="text-xl font-bold text-slate-800">إدارة طلبات الاستفسار (Leads)</h3>
          <p className="text-sm text-slate-500">العملاء الذين طلبوا التواصل عبر الموقع.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead className="bg-white text-slate-500 text-sm border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-medium">تاريخ</th>
                <th className="px-6 py-4 font-medium">العميل</th>
                <th className="px-6 py-4 font-medium">العقار المطلوب</th>
                <th className="px-6 py-4 font-medium">الحالة</th>
                <th className="px-6 py-4 font-medium text-center">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayLeads.map(l => {
                const prop = (properties || []).find(p => Number(p.id) === Number(l.propertyId));
                return (
                  <tr key={l.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-500">{new Date(l.date).toLocaleDateString('ar-EG')}</td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800">{l.name}</div>
                      <div className="text-sm text-blue-600 dir-ltr text-right">{l.phone}</div>
                      {l.message && <div className="text-xs text-slate-500 mt-1 max-w-[200px] truncate">{l.message}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">{prop?.title || 'عقار محذوف'}</td>
                    <td className="px-6 py-4">
                      <select 
                        value={l.status} 
                        onChange={(e) => handleStatusChange(l.id, e.target.value)}
                        className={`text-xs font-bold rounded-full px-3 py-1 outline-none border-0 ${l.status === 'جديد' ? 'bg-red-100 text-red-700' : l.status === 'تم التواصل' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}
                      >
                        <option value="جديد">جديد</option>
                        <option value="تم التواصل">تم التواصل</option>
                        <option value="مغلق/تم البيع">مغلق/تم البيع</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <a href={`https://wa.me/${l.phone.replace(/[^a-zA-Z0-9]/g, '')}`} target="_blank" rel="noreferrer" className="inline-block p-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 mx-1" title="واتساب"><MessageCircle size={16}/></a>
                      <button onClick={() => handleDeleteLead(l.id)} className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 mx-1"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                );
              })}
              {displayLeads.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-500">لا توجد طلبات حالياً.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const AdminProperties = () => {
    const formRef = useRef(null);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [isGeneratingSEO, setIsGeneratingSEO] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Dynamic Categories State for Form
    const [selectedCategory, setSelectedCategory] = useState(isEditingData?.category || propertyCategories[0]?.id || 'residential');

    const handleSaveProperty = async (e) => {
      e.preventDefault();
      setIsSaving(true);
      const formData = new FormData(e.target);
      const isNew = isEditingData === 'new';
      const id = isNew ? Date.now() : isEditingData.id;
      
      let finalImageUrl = formData.get('imageUrl') || isEditingData?.image || '';
      const imageFile = formData.get('imageFile');
      
      if (imageFile && imageFile.size > 0) {
        const uploadData = new FormData();
        uploadData.append('file', imageFile);
        try {
          const res = await fetch(`${API_BASE_URL}/upload.php`, { method: 'POST', body: uploadData });
          const uploadResult = await res.json();
          if (uploadResult.success) {
            finalImageUrl = uploadResult.url;
          } else {
            alert('فشل رفع الصورة: ' + uploadResult.error);
            setIsSaving(false);
            return;
          }
        } catch(e) {
          alert('خطأ في الاتصال بسيرفر الرفع. سيتم الحفظ بدون الصورة المرفوعة.');
        }
      }

      const selectedAgentId = Number(formData.get('agentId'));
      const assignedAgent = (users || []).find(u => Number(u.id) === selectedAgentId) || currentUser;
      
      // Determine English translation for selected subCategory
      const activeCat = propertyCategories.find(c => c.id === formData.get('category'));
      const subCatsAr = activeCat?.subCategoriesAr || activeCat?.subCategories || [];
      const subCatsEn = activeCat?.subCategoriesEn || [];
      const subCategoryAr = formData.get('subCategory');
      const subIndex = subCatsAr.indexOf(subCategoryAr);
      const subCategoryEn = subIndex !== -1 && subCatsEn[subIndex] ? subCatsEn[subIndex] : '';

      const newProp = {
        id: id,
        title: formData.get('title'), titleEn: formData.get('titleEn'),
        category: formData.get('category'), 
        subCategory: subCategoryAr, 
        subCategoryEn: subCategoryEn,
        transaction: formData.get('transaction'), condition: formData.get('condition'),
        city: formData.get('city'), cityEn: formData.get('cityEn'), price: Number(formData.get('price')),
        image: finalImageUrl,
        videoUrl: formData.get('videoUrl'),
        description: formData.get('description'), descriptionEn: formData.get('descriptionEn'),
        specs: {
          floor: Number(formData.get('floor')), rooms: Number(formData.get('rooms')), bathrooms: Number(formData.get('bathrooms')),
          hasMasterBath: formData.get('hasMasterBath') === 'on', hasElevator: formData.get('hasElevator') === 'on', finishing: formData.get('finishing'),
          subCategory: subCategoryAr,
          subCategoryEn: subCategoryEn,
        },
        seo: {
          title: formData.get('seoTitle'), desc: formData.get('seoDesc'), keywords: formData.get('seoKeywords')
        },
        agentId: assignedAgent.id,
        agentPhone: assignedAgent.phone || ''
      };

      try {
        await fetch(`${API_BASE_URL}/properties.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newProp)
        });
        
        if (isNew) setProperties([newProp, ...(properties || [])]);
        else setProperties((properties || []).map(p => p.id === id ? newProp : p));
        
        showSuccess(isNew ? 'تمت إضافة الوحدة' : 'تم تعديل الوحدة');
        setIsEditingData(null);
      } catch (err) { console.error(err); }
      setIsSaving(false);
    };

    const generateAIContent = async (type) => {
      const apiKey = settings.geminiApiKey;
      if (!apiKey) {
        alert('يرجى إضافة مفتاح Gemini API في الإعدادات أولاً.');
        return;
      }

      if (!formRef.current) return;
      const formData = new FormData(formRef.current);
      const title = formData.get('title');
      if (!title) { alert('يرجى إدخال عنوان الوحدة أولاً.'); return; }

      const isSEO = type === 'seo';
      isSEO ? setIsGeneratingSEO(true) : setIsGeneratingAI(true);

      const promptDesc = `أنت خبير تسويق عقاري محترف. اكتب وصف إعلاني لعقار: العنوان: ${title}, المدينة: ${formData.get('city')}, السعر: ${formData.get('price')}. الرد كـ JSON فقط بدون أي نصوص إضافية: {"descriptionAr": "...", "descriptionEn": "..."}`;
      const promptSEO = `أنت خبير SEO. استخرج للعقار التالي (العنوان: ${title}, المدينة: ${formData.get('city')}): Meta Title قصير، Meta Description، وكلمات مفتاحية Keywords. الرد كـ JSON فقط بدون أي نصوص إضافية: {"seoTitle": "...", "seoDesc": "...", "seoKeywords": "..."}`;

      try {
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contents: [{ parts: [{ text: isSEO ? promptSEO : promptDesc }] }], generationConfig: { responseMimeType: "application/json" } })
        });
        const result = await response.json();
        if(result.error) throw new Error(result.error.message);

        let text = result.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
        text = text.replace(/```json/g, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(text);
        
        if (isSEO) {
          if(parsed.seoTitle) formRef.current.querySelector('[name="seoTitle"]').value = parsed.seoTitle;
          if(parsed.seoDesc) formRef.current.querySelector('[name="seoDesc"]').value = parsed.seoDesc;
          if(parsed.seoKeywords) formRef.current.querySelector('[name="seoKeywords"]').value = parsed.seoKeywords;
        } else {
          if(parsed.descriptionAr) formRef.current.querySelector('[name="description"]').value = parsed.descriptionAr;
          if(parsed.descriptionEn) formRef.current.querySelector('[name="descriptionEn"]').value = parsed.descriptionEn;
        }
        showSuccess('تم التوليد بنجاح!');
      } catch (error) { 
        alert('حدث خطأ في الذكاء الاصطناعي: ' + error.message); 
      }
      isSEO ? setIsGeneratingSEO(false) : setIsGeneratingAI(false);
    };

    const handleDelete = async (id) => {
      if(window.confirm('هل أنت متأكد من مسح هذه الوحدة؟')){
        try {
          await fetch(`${API_BASE_URL}/properties.php?id=${id}`, { method: 'DELETE' });
          setProperties((properties || []).filter(p => p.id !== id)); 
          showSuccess('تم مسح الوحدة');
        } catch (err) { console.error(err); }
      }
    };

    const displayProps = currentUser.role === 'مدير' ? (properties || []) : (properties || []).filter(p => Number(p.agentId) === Number(currentUser.id));
    
    const activeCat = propertyCategories.find(c => c.id === selectedCategory);
    const activeSubCategoriesAr = activeCat?.subCategoriesAr || activeCat?.subCategories || [];

    if (isEditingData !== null) {
      return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 animate-in fade-in zoom-in-95 duration-200 max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">{isEditingData === 'new' ? 'إضافة وحدة جديدة' : 'تعديل الوحدة'}</h2>
            <button onClick={() => setIsEditingData(null)} className="text-slate-500 hover:text-red-500"><X size={24} /></button>
          </div>
          <form ref={formRef} onSubmit={handleSaveProperty} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-slate-50 rounded-xl border border-slate-100">
              <h3 className="col-span-full font-bold text-blue-600 border-b pb-2">المعلومات الأساسية</h3>
              <div className="space-y-2 md:col-span-2"><label className="text-sm font-medium">عنوان الوحدة</label><input required name="title" defaultValue={isEditingData?.title} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" /></div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">القسم الرئيسي</label>
                <select name="category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
                  {propertyCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.nameAr}</option>)}
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">النوع</label>
                <select name="subCategory" defaultValue={isEditingData?.subCategory || activeSubCategoriesAr[0]} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
                  {activeSubCategoriesAr.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                </select>
              </div>

              <div className="space-y-2"><label className="text-sm font-medium">العملية</label><select name="transaction" defaultValue={isEditingData?.transaction || 'بيع'} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none"><option value="بيع">بيع</option><option value="إيجار">إيجار</option></select></div>
              <div className="space-y-2"><label className="text-sm font-medium">المدينة</label><input required name="city" defaultValue={isEditingData?.city} placeholder="مثال: القاهرة" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" /></div>
              <div className="space-y-2"><label className="text-sm font-medium">السعر (ج.م)</label><input required type="number" name="price" defaultValue={isEditingData?.price} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" /></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-indigo-50/50 rounded-xl border border-indigo-100">
              <div className="col-span-full flex justify-between items-center border-b border-indigo-200 pb-2">
                <h3 className="font-bold text-indigo-700">الوصف التسويقي</h3>
                <button type="button" onClick={() => generateAIContent('desc')} disabled={isGeneratingAI} className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors"><Sparkles size={14}/> توليد بالذكاء الاصطناعي</button>
              </div>
              <div className="space-y-2"><label className="text-sm font-medium text-slate-700">عربي</label><textarea required name="description" defaultValue={isEditingData?.description} rows="4" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none"></textarea></div>
              <div className="space-y-2"><label className="text-sm font-medium text-slate-700">English Title & Desc</label>
                <input name="titleEn" defaultValue={isEditingData?.titleEn} placeholder="English Title" className="w-full px-4 py-2 rounded-xl border border-slate-200 outline-none dir-ltr text-left mb-2" />
                <textarea name="descriptionEn" defaultValue={isEditingData?.descriptionEn} rows="2" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none dir-ltr text-left"></textarea>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-slate-50 rounded-xl border border-slate-100">
               <div className="col-span-full flex justify-between items-center border-b pb-2">
                <h3 className="font-bold text-slate-700">أرشفة محركات البحث (SEO)</h3>
                <button type="button" onClick={() => generateAIContent('seo')} disabled={isGeneratingSEO} className="flex items-center gap-1 text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg hover:bg-slate-700"><Sparkles size={14}/> استخراج تلقائي</button>
              </div>
              <div className="space-y-2"><label className="text-sm text-slate-600">Meta Title</label><input name="seoTitle" defaultValue={isEditingData?.seo?.title} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" /></div>
              <div className="space-y-2"><label className="text-sm text-slate-600">Keywords (مفصولة بفاصلة)</label><input name="seoKeywords" defaultValue={isEditingData?.seo?.keywords} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" /></div>
              <div className="space-y-2 col-span-full"><label className="text-sm text-slate-600">Meta Description</label><textarea name="seoDesc" defaultValue={isEditingData?.seo?.desc} rows="2" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"></textarea></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-slate-50 rounded-xl border border-slate-100">
              <h3 className="col-span-full font-bold text-blue-600 border-b pb-2">الميديا والمواصفات</h3>
              
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium flex items-center gap-2"><UploadCloud size={16}/> صورة العقار (رفع من الجهاز)</label>
                <input type="file" name="imageFile" accept="image/*" className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white" />
                <label className="text-sm font-medium mt-3 block">أو ضع رابط الصورة مباشرة (URL)</label>
                <input name="imageUrl" defaultValue={isEditingData?.image} placeholder="https://..." className="w-full px-4 py-2 rounded-xl border border-slate-200 dir-ltr text-left" />
              </div>

              <div className="space-y-2"><label className="text-sm font-medium">رابط فيديو (يوتيوب)</label><input name="videoUrl" defaultValue={isEditingData?.videoUrl} placeholder="https://youtube.com/..." className="w-full px-4 py-2 rounded-xl border border-slate-200 dir-ltr text-left" /></div>
              <div className="space-y-2"><label className="text-sm">الدور</label><input required type="number" name="floor" defaultValue={isEditingData?.specs?.floor ?? 1} className="w-full px-4 py-2 rounded-xl border border-slate-200" /></div>
              <div className="space-y-2"><label className="text-sm">الغرف</label><input required type="number" name="rooms" defaultValue={isEditingData?.specs?.rooms ?? 0} className="w-full px-4 py-2 rounded-xl border border-slate-200" /></div>
              <div className="space-y-2"><label className="text-sm">الحمامات</label><input required type="number" name="bathrooms" defaultValue={isEditingData?.specs?.bathrooms ?? 1} className="w-full px-4 py-2 rounded-xl border border-slate-200" /></div>
              <div className="space-y-2"><label className="text-sm">التشطيب</label><select name="finishing" defaultValue={isEditingData?.specs?.finishing} className="w-full px-4 py-2 rounded-xl border border-slate-200"><option value="تشطيب كامل">كامل</option><option value="نصف تشطيب">نصف تشطيب</option><option value="أهالي">أهالي</option></select></div>
              <div className="flex items-center gap-3 mt-8"><input type="checkbox" name="hasElevator" defaultChecked={isEditingData?.specs?.hasElevator} id="elev" className="w-5 h-5 rounded text-blue-600" /><label htmlFor="elev" className="text-sm">أسانسير</label></div>
              <div className="flex items-center gap-3 mt-8"><input type="checkbox" name="hasMasterBath" defaultChecked={isEditingData?.specs?.hasMasterBath} id="mbath" className="w-5 h-5 rounded text-blue-600" /><label htmlFor="mbath" className="text-sm">حمام ماستر</label></div>
            </div>

            {currentUser.role === 'مدير' && (
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                <label className="text-sm font-bold text-slate-700 block mb-2">البائع المسؤول (سيتم توجيه طلبات الواتساب إليه)</label>
                <select name="agentId" defaultValue={isEditingData?.agentId || currentUser.id} className="w-full max-w-xs px-4 py-2 rounded-xl border border-slate-200">
                  {(users || []).map(u => <option key={u.id} value={u.id}>{u.name} - {u.phone}</option>)}
                </select>
              </div>
            )}

            <div className="flex gap-4 pt-4 border-t">
              <button type="submit" disabled={isSaving} className="flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold disabled:opacity-70 min-w-[150px]">
                {isSaving ? <Loader2 size={18} className="animate-spin"/> : 'حفظ العقار'}
              </button>
              <button type="button" onClick={() => setIsEditingData(null)} className="bg-slate-100 px-8 py-3 rounded-xl font-bold">إلغاء</button>
            </div>
          </form>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="text-xl font-bold text-slate-800">الوحدات العقارية</h3>
          <button onClick={() => setIsEditingData('new')} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            <Plus size={18} /> إضافة وحدة
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead className="bg-white text-slate-500 text-sm border-b border-slate-100">
              <tr>
                <th className="px-6 py-4">الوحدة</th>
                <th className="px-6 py-4">السعر</th>
                <th className="px-6 py-4">النوع</th>
                {currentUser.role === 'مدير' && <th className="px-6 py-4">البائع</th>}
                <th className="px-6 py-4 text-center">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayProps.map(prop => {
                 const agent = (users || []).find(u => Number(u.id) === Number(prop.agentId));
                 const catName = propertyCategories.find(c => c.id === prop.category)?.nameAr || prop.category;
                 return (
                  <tr key={prop.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {prop.image ? <img src={prop.image} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center"><Building2 size={20} className="text-slate-400"/></div>}
                        <div className="font-bold text-slate-800 truncate max-w-[200px]">{prop.title}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-blue-600 whitespace-nowrap">{prop.price.toLocaleString()} ج</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="block font-medium">{catName}</span>
                      <span className="text-xs text-slate-500">{prop.subCategory}</span>
                    </td>
                    {currentUser.role === 'مدير' && (
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {agent ? agent.name : '-'}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => setIsEditingData(prop)} className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"><Edit size={16}/></button>
                        <button onClick={() => handleDelete(prop.id)} className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100"><Trash2 size={16}/></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {displayProps.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-500">لا توجد وحدات مضافة بعد.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const AdminClients = () => {
    const handleSaveClient = async (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const isNew = isEditingData === 'new';
      const id = isNew ? Date.now() : isEditingData.id;
      const newClient = {
        id: id, name: formData.get('name'), phone: formData.get('phone'), propertyId: Number(formData.get('propertyId')),
        agentId: Number(formData.get('agentId')), commission: Number(formData.get('commission')), date: formData.get('date') || new Date().toISOString().split('T')[0]
      };
      try {
        await fetch(`${API_BASE_URL}/clients.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newClient)
        });
        if (isNew) setClients([newClient, ...(clients || [])]);
        else setClients((clients || []).map(c => c.id === id ? newClient : c));
        
        showSuccess(isNew ? 'تم إضافة العميل' : 'تم التعديل'); setIsEditingData(null);
      } catch (err) { console.error(err); }
    };

    const handleDeleteClient = async (id) => {
      if(window.confirm('مسح العملية؟')) {
        try {
          await fetch(`${API_BASE_URL}/clients.php?id=${id}`, { method: 'DELETE' });
          setClients((clients || []).filter(c => c.id !== id));
          showSuccess('تم مسح البيعة');
        } catch (err) { console.error(err); }
      }
    };

    if (isEditingData !== null) {
      return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 max-w-2xl animate-in fade-in">
          <h2 className="text-xl font-bold mb-6">{isEditingData === 'new' ? 'إضافة بيعة/عميل' : 'تعديل البيعة'}</h2>
          <form onSubmit={handleSaveClient} className="space-y-4">
             <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><label className="text-sm font-medium">الاسم</label><input required name="name" defaultValue={isEditingData?.name} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" /></div>
              <div className="space-y-2"><label className="text-sm font-medium">الهاتف</label><input required name="phone" defaultValue={isEditingData?.phone} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none dir-ltr text-right" /></div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">الوحدة</label>
              <select required name="propertyId" defaultValue={isEditingData?.propertyId} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
                {(properties || []).map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
             <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">البائع</label>
                <select required name="agentId" defaultValue={isEditingData?.agentId} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
                  {(users || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="space-y-2"><label className="text-sm font-medium">العمولة (ج.م)</label><input required type="number" name="commission" defaultValue={isEditingData?.commission} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" /></div>
            </div>
            <div className="flex gap-4 pt-4 mt-6"><button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold">حفظ</button><button type="button" onClick={() => setIsEditingData(null)} className="bg-slate-100 px-8 py-3 rounded-xl font-bold">إلغاء</button></div>
          </form>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b flex justify-between bg-slate-50">
          <h3 className="text-xl font-bold">العملاء والمبيعات المنفذة</h3>
          <button onClick={() => setIsEditingData('new')} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><Plus size={18} /> إضافة</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-white text-slate-500 border-b"><tr><th className="p-4">العميل</th><th className="p-4">الوحدة</th><th className="p-4">البائع</th><th className="p-4">العمولة</th><th className="p-4 text-center">إجراءات</th></tr></thead>
            <tbody className="divide-y">{(clients || []).map(c => (<tr key={c.id} className="hover:bg-slate-50"><td className="p-4 font-bold">{c.name}</td><td className="p-4">{(properties || []).find(p=>p.id===c.propertyId)?.title}</td><td className="p-4">{(users || []).find(u=>u.id===c.agentId)?.name}</td><td className="p-4 text-green-600 font-bold">{c.commission?.toLocaleString()} ج</td>
            <td className="p-4 text-center">
                <button onClick={() => setIsEditingData(c)} className="p-2 text-blue-600 bg-blue-50 rounded-lg mx-1"><Edit size={16}/></button>
                <button onClick={() => handleDeleteClient(c.id)} className="p-2 text-red-600 bg-red-50 rounded-lg mx-1"><Trash2 size={16}/></button>
            </td></tr>))}
            {(clients || []).length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-500">لا توجد مبيعات.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const AdminUsers = () => {
    if (currentUser.role !== 'مدير') return null;

    const handleSaveUser = async (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const isNew = isEditingData === 'new';
      const id = isNew ? Date.now() : isEditingData.id;

      const newUser = {
        id: id,
        name: formData.get('name'),
        username: formData.get('username'),
        password: formData.get('password'),
        role: formData.get('role'),
        phone: formData.get('phone')
      };

      try {
        await fetch(`${API_BASE_URL}/users.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newUser)
        });
        if (isNew) setUsers([...(users || []), newUser]);
        else setUsers((users || []).map(u => u.id === id ? newUser : u));
        
        showSuccess(isNew ? 'تم إضافة الموظف' : 'تم تعديل بيانات الموظف');
        setIsEditingData(null);
      } catch (err) { console.error(err); }
    };

    const handleDeleteUser = async (id) => {
      if(window.confirm('هل أنت متأكد من مسح هذا الموظف؟')) {
        try {
          await fetch(`${API_BASE_URL}/users.php?id=${id}`, { method: 'DELETE' });
          setUsers((users || []).filter(u => u.id !== id));
          showSuccess('تم مسح الموظف');
        } catch (err) { console.error(err); }
      }
    };

    if (isEditingData !== null && adminTab === 'users') {
      return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 max-w-2xl animate-in fade-in zoom-in-95 duration-200">
          <h2 className="text-xl font-bold text-slate-800 mb-6">{isEditingData === 'new' ? 'إضافة موظف جديد' : 'تعديل بيانات الموظف'}</h2>
          <form onSubmit={handleSaveUser} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">الاسم بالكامل</label>
              <input required name="name" defaultValue={isEditingData?.name} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">اسم المستخدم الدخول</label>
                <input required name="username" defaultValue={isEditingData?.username} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none dir-ltr text-right" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">كلمة المرور</label>
                <input required name="password" defaultValue={isEditingData?.password} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none dir-ltr text-right" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">رقم الواتساب</label>
              <input required name="phone" defaultValue={isEditingData?.phone} placeholder="201xxxxxxxxx" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none dir-ltr text-right" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">الصلاحية</label>
              <select name="role" defaultValue={isEditingData?.role || 'بائع'} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
                <option value="بائع">بائع (موظف مبيعات)</option>
                <option value="مدير">مدير (صلاحيات كاملة)</option>
              </select>
            </div>
            <div className="flex gap-4 pt-4 mt-6 border-t border-slate-100">
              <button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold">حفظ</button>
              <button type="button" onClick={() => setIsEditingData(null)} className="bg-slate-100 text-slate-700 px-8 py-3 rounded-xl font-bold">إلغاء</button>
            </div>
          </form>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="text-xl font-bold text-slate-800">فريق العمل (المديرين والبائعين)</h3>
          <button onClick={() => setIsEditingData('new')} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium">
            <Plus size={18} /> إضافة عضو
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead className="bg-white text-slate-500 text-sm border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-medium">الاسم</th>
                <th className="px-6 py-4 font-medium">الدخول</th>
                <th className="px-6 py-4 font-medium">الواتساب</th>
                <th className="px-6 py-4 font-medium">الصلاحية</th>
                <th className="px-6 py-4 font-medium text-center">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(users || []).map(u => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-bold text-slate-800">{u.name}</td>
                  <td className="px-6 py-4 text-slate-500 dir-ltr text-right">{u.username}</td>
                  <td className="px-6 py-4 text-slate-500 dir-ltr text-right">{u.phone}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${u.role === 'مدير' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => setIsEditingData(u)} className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"><Edit size={16}/></button>
                      <button onClick={() => {if(u.id !== currentUser.id) handleDeleteUser(u.id)}} className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50" disabled={u.id === currentUser.id}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {(users || []).length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-500">لا يوجد مستخدمين.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const AdminSettings = () => {
    const [localMenuItems, setLocalMenuItems] = useState(settings.menuItems || initialSettings.menuItems);
    const [localCategories, setLocalCategories] = useState(propertyCategories);

    const handleSaveSettings = async (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const newSettings = {
        siteNameAr: formData.get('siteNameAr'), siteNameEn: formData.get('siteNameEn'),
        aboutUs: formData.get('aboutUs'), aboutUsEn: formData.get('aboutUsEn'), contactEmail: formData.get('contactEmail'),
        contactPhone: formData.get('contactPhone'), appointmentPhone: formData.get('appointmentPhone'), address: formData.get('address'),
        addressEn: formData.get('addressEn'), logoUrl: formData.get('logoUrl'), facebook: formData.get('facebook'),
        twitter: formData.get('twitter'), instagram: formData.get('instagram'), geminiApiKey: formData.get('geminiApiKey'),
        menuItems: localMenuItems,
        propertyCategories: localCategories
      };
      try { 
        await fetch(`${API_BASE_URL}/settings.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newSettings)
        });
        setSettings(newSettings); showSuccess('تم الحفظ بنجاح'); 
      } catch (err) { console.error(err); }
    };

    const addMenuItem = () => setLocalMenuItems([...localMenuItems, { id: Date.now(), labelAr: '', labelEn: '', link: '', type: 'internal' }]);
    const updateMenuItem = (id, field, value) => setLocalMenuItems(localMenuItems.map(item => item.id === id ? { ...item, [field]: value } : item));
    const removeMenuItem = (id) => setLocalMenuItems(localMenuItems.filter(item => item.id !== id));

    const updateSubCategories = (catId, lang, subsString) => {
      const subs = subsString.split(',').map(s => s.trim()).filter(Boolean);
      setLocalCategories(localCategories.map(cat => {
        if (cat.id !== catId) return cat;
        if (lang === 'ar') return { ...cat, subCategoriesAr: subs, subCategories: subs };
        return { ...cat, subCategoriesEn: subs };
      }));
    };

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 max-w-4xl">
        <h2 className="text-xl font-bold mb-6">إعدادات الموقع</h2>
        <form onSubmit={handleSaveSettings} className="space-y-6">
          
          <div className="p-4 bg-slate-50 rounded-xl space-y-4">
            <h3 className="font-bold text-blue-600 border-b pb-2">إدارة أقسام الوحدات</h3>
            <p className="text-xs text-slate-500 mb-4">قم بتحديد الأنواع الفرعية لكل قسم، مفصولة بفاصلة (,)</p>
            {localCategories.map(cat => {
              const subsAr = cat.subCategoriesAr || cat.subCategories || [];
              const subsEn = cat.subCategoriesEn || [];
              return (
                <div key={cat.id} className="space-y-2 border border-slate-200 p-4 rounded-lg bg-white">
                  <label className="text-sm font-bold text-slate-700">{cat.nameAr} ({cat.nameEn})</label>
                  <input 
                    type="text" 
                    defaultValue={subsAr.join(', ')} 
                    onChange={(e) => updateSubCategories(cat.id, 'ar', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border outline-none text-sm focus:ring-1 focus:ring-blue-500" 
                    placeholder="الأقسام (عربي) مفصولة بفاصلة: شقق, بيوت..."
                  />
                  <input 
                    type="text" 
                    defaultValue={subsEn.join(', ')} 
                    onChange={(e) => updateSubCategories(cat.id, 'en', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border outline-none text-sm dir-ltr text-left focus:ring-1 focus:ring-blue-500" 
                    placeholder="الأقسام (English) مفصولة بفاصلة: Apartments, Houses..."
                  />
                </div>
              );
            })}
          </div>

          <div className="p-4 bg-indigo-50/50 rounded-xl space-y-4 border border-indigo-100">
            <h3 className="font-bold text-indigo-700 border-b border-indigo-200 pb-2 flex items-center gap-2"><Sparkles size={18}/> مفتاح الذكاء الاصطناعي (Gemini API)</h3>
            <p className="text-xs text-slate-500">احصل على المفتاح مجاناً من Google AI Studio لتفعيل ميزة كتابة الأوصاف التلقائية للعقارات.</p>
            <div className="space-y-2">
              <input name="geminiApiKey" defaultValue={settings.geminiApiKey} type="password" placeholder="AIzaSy..." className="w-full px-4 py-2 rounded-xl border border-slate-200 dir-ltr text-left" />
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl space-y-4">
            <h3 className="font-bold border-b pb-2 text-blue-600">اسم الموقع (Branding)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><label className="text-sm font-medium">اسم الموقع (عربي)</label><input required name="siteNameAr" defaultValue={settings.siteNameAr || initialSettings.siteNameAr} className="w-full px-4 py-2 rounded-xl border outline-none focus:ring-1 focus:ring-blue-500" /></div>
              <div className="space-y-2"><label className="text-sm font-medium">اسم الموقع (English)</label><input required name="siteNameEn" defaultValue={settings.siteNameEn || initialSettings.siteNameEn} className="w-full px-4 py-2 rounded-xl border outline-none focus:ring-1 focus:ring-blue-500 dir-ltr text-left" /></div>
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-bold text-blue-600">إدارة القائمة الرئيسية (Menu)</h3>
              <button type="button" onClick={addMenuItem} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-700"><Plus size={14}/> إضافة عنصر</button>
            </div>
            <div className="space-y-3">
              {localMenuItems.map((item, idx) => (
                <div key={item.id} className="flex flex-wrap items-center gap-2 bg-white p-3 rounded-lg border border-slate-200 shadow-sm relative">
                   <div className="absolute -right-2 -top-2 bg-slate-200 text-slate-600 text-xs w-5 h-5 rounded-full flex items-center justify-center">{idx + 1}</div>
                   <input value={item.labelAr} onChange={e => updateMenuItem(item.id, 'labelAr', e.target.value)} placeholder="الاسم (عربي)" className="flex-1 min-w-[120px] px-3 py-2 border rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500" required />
                   <input value={item.labelEn} onChange={e => updateMenuItem(item.id, 'labelEn', e.target.value)} placeholder="الاسم (English)" className="flex-1 min-w-[120px] px-3 py-2 border rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500 dir-ltr text-left" required />
                   <select value={item.type} onChange={e => { updateMenuItem(item.id, 'type', e.target.value); updateMenuItem(item.id, 'link', ''); }} className="px-3 py-2 border rounded-md text-sm outline-none bg-slate-50 text-slate-700">
                      <option value="internal">رابط داخلي</option>
                      <option value="external">رابط خارجي</option>
                   </select>
                   {item.type === 'internal' ? (
                      <select value={item.link} onChange={e => updateMenuItem(item.id, 'link', e.target.value)} className="flex-1 min-w-[120px] px-3 py-2 border rounded-md text-sm outline-none font-medium text-slate-700">
                        <option value="" disabled>اختر الصفحة...</option>
                        <option value="home">الرئيسية</option>
                        <option value="properties">وحدات سكنية</option>
                        <option value="shops">تجاري وإداري</option>
                        <option value="about">من نحن</option>
                        <option value="contact">اتصل بنا</option>
                      </select>
                   ) : (
                      <input value={item.link} onChange={e => updateMenuItem(item.id, 'link', e.target.value)} placeholder="https://..." className="flex-1 min-w-[120px] px-3 py-2 border rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500 dir-ltr text-left" required />
                   )}
                   <button type="button" onClick={() => removeMenuItem(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"><Trash2 size={18}/></button>
                </div>
              ))}
            </div>
          </div>

           <div className="p-4 bg-slate-50 rounded-xl space-y-4"><h3 className="font-bold border-b pb-2">الهوية وروابط السوشيال ميديا</h3>
            <div className="space-y-2"><label className="text-sm font-medium">رابط اللوجو</label><input name="logoUrl" defaultValue={settings.logoUrl} className="w-full px-4 py-2 rounded-xl border dir-ltr text-left" /></div>
            <div className="grid grid-cols-3 gap-4">
              <input name="facebook" defaultValue={settings.facebook} placeholder="Facebook URL" className="w-full px-4 py-2 rounded-xl border dir-ltr text-left text-sm" />
              <input name="twitter" defaultValue={settings.twitter} placeholder="Twitter URL" className="w-full px-4 py-2 rounded-xl border dir-ltr text-left text-sm" />
              <input name="instagram" defaultValue={settings.instagram} placeholder="Instagram URL" className="w-full px-4 py-2 rounded-xl border dir-ltr text-left text-sm" />
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl space-y-4"><h3 className="font-bold border-b pb-2">التواصل</h3>
             <div className="grid grid-cols-2 gap-4">
              <input required name="contactPhone" defaultValue={settings.contactPhone} placeholder="الهاتف" className="w-full px-4 py-2 rounded-xl border dir-ltr text-right" />
              <input required name="appointmentPhone" defaultValue={settings.appointmentPhone} placeholder="واتساب الإدارة" className="w-full px-4 py-2 rounded-xl border dir-ltr text-right" />
              <input required name="contactEmail" defaultValue={settings.contactEmail} placeholder="الإيميل" className="w-full px-4 py-2 rounded-xl border dir-ltr text-right" />
              <input required name="address" defaultValue={settings.address} placeholder="العنوان" className="w-full px-4 py-2 rounded-xl border" />
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl space-y-4"><h3 className="font-bold border-b pb-2">من نحن</h3>
             <textarea required name="aboutUs" defaultValue={settings.aboutUs} rows="3" className="w-full px-4 py-2 rounded-xl border" placeholder="عربي"></textarea>
             <textarea name="aboutUsEn" defaultValue={settings.aboutUsEn} rows="3" className="w-full px-4 py-2 rounded-xl border dir-ltr text-left" placeholder="English"></textarea>
          </div>
          <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-md">حفظ التعديلات الشاملة</button>
        </form>
      </div>
    );
  };

  if (currentView === 'admin_login') {
    return (
      <div dir="rtl" className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100 w-full max-w-md">
          <div className="text-center mb-8"><Building2 className="h-12 w-12 text-blue-600 mx-auto mb-3" /><h1 className="text-2xl font-bold text-slate-900">إدارة {siteBrand}</h1></div>
          {loginError && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6">{loginError}</div>}
          <form onSubmit={handleLogin} className="space-y-5">
            <input required name="username" placeholder="اسم المستخدم" className="w-full px-4 py-3 rounded-xl border dir-ltr text-right" />
            <input required name="password" type="password" placeholder="كلمة المرور" className="w-full px-4 py-3 rounded-xl border dir-ltr text-right" />
            <button type="submit" className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold shadow-md">دخول</button>
          </form>
          <button onClick={() => navigate('home')} className="mt-6 w-full text-center text-slate-500 hover:text-blue-600 text-sm font-medium flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> العودة للموقع
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'admin_dashboard') {
    if (!currentUser) return null;
    return (
      <div dir="rtl" className="min-h-screen bg-slate-50 font-sans flex text-slate-800">
        <AdminSidebar />
        <main className="flex-1 md:mr-64 flex flex-col h-screen overflow-hidden">
          <header className="md:hidden bg-white border-b p-4 flex justify-between shadow-sm z-10"><span className="font-bold">الإدارة</span><button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}><Menu /></button></header>
          {isMobileMenuOpen && (
            <div className="md:hidden fixed inset-0 top-16 bg-slate-900 z-50 p-4 text-white">
               <nav className="space-y-2">
                 <button onClick={() => {setAdminTab('overview'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">نظرة عامة</button>
                 <button onClick={() => {setAdminTab('properties'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">الوحدات</button>
                 <button onClick={() => {setAdminTab('leads'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">الطلبات</button>
                 {currentUser?.role === 'مدير' && (
                   <>
                    <button onClick={() => {setAdminTab('clients'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">العملاء</button>
                    <button onClick={() => {setAdminTab('users'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">فريق العمل</button>
                    <button onClick={() => {setAdminTab('settings'); setIsMobileMenuOpen(false);}} className="w-full text-right p-3 bg-slate-800 rounded">الإعدادات</button>
                   </>
                 )}
                 <button onClick={handleLogout} className="w-full text-right p-3 bg-red-900 rounded mt-4">خروج</button>
               </nav>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4 md:p-8">
            {successMsg && <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-6 py-2 rounded-full z-50"><Check size={18} className="inline mr-1"/>{successMsg}</div>}
            <div className="max-w-6xl mx-auto">
               <div className="mb-6 flex justify-between items-center"><h2 className="text-2xl font-bold">{adminTab === 'overview' ? 'نظرة عامة' : adminTab === 'properties' ? 'الوحدات' : adminTab === 'leads' ? 'الطلبات' : adminTab === 'clients' ? 'المبيعات' : adminTab === 'users' ? 'فريق العمل' : 'الإعدادات'}</h2><button onClick={() => navigate('home')} className="bg-slate-200 hover:bg-slate-300 transition-colors px-4 py-2 rounded-lg text-sm font-bold shadow-sm">الموقع كزائر</button></div>
               {adminTab === 'overview' && <AdminOverview />}
               {adminTab === 'properties' && <AdminProperties />}
               {adminTab === 'leads' && <AdminLeads />}
               {adminTab === 'clients' && <AdminClients />}
               {adminTab === 'users' && <AdminUsers />}
               {adminTab === 'settings' && <AdminSettings />}
            </div>
          </div>
        </main>
      </div>
    );
  }

  let publicProps = properties || [];
  if (currentView === 'properties') publicProps = (properties || []).filter(p => p.category === 'residential');
  if (currentView === 'shops') publicProps = (properties || []).filter(p => p.category === 'commercial');
  if (currentView === 'wishlist') publicProps = (properties || []).filter(p => wishlist.includes(p.id));

  let filteredProps = publicProps;
  if (selectedFilterCity) filteredProps = filteredProps.filter(p => (isRTL ? p.city : (p.cityEn || p.city)) === selectedFilterCity);
  if (selectedSubCategory) filteredProps = filteredProps.filter(p => p.subCategory === selectedSubCategory);
  if (filters.minPrice) filteredProps = filteredProps.filter(p => p.price >= Number(filters.minPrice));
  if (filters.maxPrice) filteredProps = filteredProps.filter(p => p.price <= Number(filters.maxPrice));
  if (filters.minRooms) filteredProps = filteredProps.filter(p => p.specs?.rooms >= Number(filters.minRooms));

  let subCategoryDisplayName = selectedSubCategory;
  if (selectedSubCategory && !isRTL) {
    for (const cat of propertyCategories) {
      const arrAr = cat.subCategoriesAr || cat.subCategories || [];
      const idx = arrAr.indexOf(selectedSubCategory);
      if (idx !== -1 && cat.subCategoriesEn && cat.subCategoriesEn[idx]) {
        subCategoryDisplayName = cat.subCategoriesEn[idx];
        break;
      }
    }
  }

  return (
    <PublicLayout>
      {selectedProperty ? (
        <PropertyDetails property={selectedProperty} onBack={() => setSelectedProperty(null)} showSuccess={showSuccess} />
      ) : (
        <>
          {currentView === 'home' && (
            <>
              <div className="relative bg-slate-900 text-white h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-40" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=2000&q=80)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
                <div className="relative z-10 text-center max-w-3xl px-4">
                  <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('search_dream')} <span className="text-blue-500">{siteBrand}</span></h1>
                  <p className="text-lg md:text-xl text-slate-300 mb-10">{t('best_offers')}</p>
                  <div className="flex gap-4 justify-center">
                    <button onClick={() => navigate('properties')} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-full font-bold shadow-lg">{t('browse_props')}</button>
                  </div>
                </div>
              </div>
              <div className="py-20 px-4 max-w-7xl mx-auto">
                <h2 className="text-3xl font-bold text-center mb-10">{t('latest_added')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">{(properties || []).slice(0, 3).map(prop => <PropertyCard key={prop.id} property={prop} />)}</div>
              </div>
            </>
          )}

          {(currentView === 'properties' || currentView === 'shops' || currentView === 'wishlist') && (
            <div className="py-12 px-4 max-w-7xl mx-auto animate-in fade-in">
              <div className="mb-8 border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="text-3xl font-bold text-slate-900">
                    {currentView === 'wishlist' ? t('wishlist') : currentView === 'properties' ? t('all_props') : t('all_shops')}
                    {selectedSubCategory && ` - ${subCategoryDisplayName}`}
                  </h1>
                  <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg font-medium"><Filter size={18}/> {t('filters')}</button>
                </div>
                
                {showFilters && (
                  <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-2 md:grid-cols-4 gap-4 animate-in slide-in-from-top-2">
                    <select value={selectedFilterCity} onChange={(e) => setSelectedFilterCity(e.target.value)} className="p-2 border rounded-lg outline-none text-slate-700">
                      <option value="">{t('all_cities')}</option>
                      {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" placeholder={t('priceMin')} value={filters.minPrice} onChange={e=>setFilters({...filters, minPrice: e.target.value})} className="p-2 border rounded-lg outline-none text-slate-700" />
                    <input type="number" placeholder={t('priceMax')} value={filters.maxPrice} onChange={e=>setFilters({...filters, maxPrice: e.target.value})} className="p-2 border rounded-lg outline-none text-slate-700" />
                    <input type="number" placeholder={t('minRooms')} value={filters.minRooms} onChange={e=>setFilters({...filters, minRooms: e.target.value})} className="p-2 border rounded-lg outline-none text-slate-700" />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProps.map(prop => <PropertyCard key={prop.id} property={prop} />)}
              </div>
              {filteredProps.length === 0 && <div className="text-center py-20 text-slate-500"><Building2 className="mx-auto h-16 w-16 text-slate-300 mb-4" /><p className="text-xl">{t('no_units')}</p></div>}
            </div>
          )}

          {currentView === 'about' && (
             <div className="py-20 px-4 max-w-4xl mx-auto text-center"><h1 className="text-4xl font-bold mb-8">{t('about')}</h1><div className="bg-white p-12 rounded-3xl shadow-sm border"><p className="text-lg leading-loose whitespace-pre-line text-slate-700">{isRTL ? settings.aboutUs : settings.aboutUsEn}</p></div></div>
          )}
        </>
      )}
    </PublicLayout>
  );
}